import React, { useState, useCallback } from "react";
import {
  Typography,
  Box,
  Fab,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";

import CandidateProfileForm from "./CandidateProfileForm";
import CandidateCurrentJobForm from "./CandidateCurrentJob";
import CandidateLocationForm from "./CandidateLocationForm";
import CandidateLookingFor from "./CandidatesLookingFor";
import CandidateContactsForm from "./CandidateContacts";
import CandidateJobStatusForm from "./CandidatesJobStatusForm";
import CandidatesSkillForm from "./CandidatesSkillForm";
import CandidateSocialForm from "./CandidateSocialForm";
import { createCandidate } from "../../api/services/candidate";
import { useSnackbar } from "notistack";
import { useNavigate } from "react-router-dom";
import { PersonAdd } from "@mui/icons-material";
import SuccessModal from "./SuccessModal";

function CandidateForm() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    gender: null,
    birthdate: null,
    profile_photo: null,
    address_1: '',
    address_2: '',
    postcode: '',
    base_location: null,
    area_list: [],
    is_all_zones: false,
    is_my_zones: false,
    position_held: '',
    nationality: null,
    current_salary: '',
    notice_required: null,
    work_permit: false,
    current_employer: '',
    skill_1: '',
    shifts: '',
    equal_opportunities: '',
    other_area_or_speciality: '',
    primary_email: '',
    secondary_email: '',
    home_phone: '',
    mobile_phone: '',
    contact_number: '',
    skype: '',
    facebook: '',
    twitter: '',
    linkedin: '',
    salary_range: null,
    hourly_rate: null,
    requested_region: null,
    weekly_contracted_hours: '',
    maximum_hours: '',
    rating: 0,
    interview_status: null,
    source: null,
    status: 'pending',
    role: null,
    work_type: null,
    nmc_pin: null,
    payroll_number: null,
    cv: null,
    documents: [],
    start_date: null,
    end_date: null,
    user: null,
    system_role: null,
  });
  const { enqueueSnackbar } = useSnackbar();
  const [selectedArea, setSelectedArea] = useState([]);
  const navigate = useNavigate();
  const [showRedirectModal, setShowRedirectModal] = useState(false);
  const [candidateData, setCandidateData] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const handleChange = useCallback((e) => {
    const { name, type, value, checked } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: type === "checkbox" ? checked : value,
    }));
  }, []);

  const handleSelectAreaChange = useCallback((options) => {
    const selectedValues = Array.isArray(options) ? options : [];
    setSelectedArea(selectedValues);
    const areaIds = selectedValues.map((opt) => opt.value || opt.id);
    setFormData((prev) => ({
      ...prev,
      area_list: areaIds,
    }));
  }, []);

  const handleImageUpload = useCallback((e) => {
    const { name, value, fileData } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
      [`${name}_data`]: fileData,
    }));
  }, []);

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    
    if (!formData.base_location) {
      enqueueSnackbar("Base location is required", { variant: "error" });
      return;
    }
    
    if (!formData.area_list || formData.area_list.length === 0) {
      enqueueSnackbar("At least one area is required", { variant: "error" });
      return;
    }
    
    if (!formData.primary_email) {
      enqueueSnackbar("Email is required", { variant: "error" });
      return;
    }
    
    setSubmitting(true);
    
    try {
      const { location, areas, ...submitData } = formData;
      
      Object.keys(submitData).forEach(key => {
        if (submitData[key] === undefined || submitData[key] === '') {
          submitData[key] = null;
        }
      });
      
      const response = await createCandidate(submitData);
      
      if (response.status === 201) {
        enqueueSnackbar("Account created successfully!", {
          variant: "success",
        });
        
        setCandidateData({
          userName: `${submitData.first_name || ''} ${submitData.last_name || ''}`.trim() || 'User',
          userEmail: submitData.primary_email,
          username: submitData.primary_email?.split('@')[0] || '',
          userRole: submitData.role || 'Carer',
        });
        
        setShowRedirectModal(true);
      }
    } catch (error) {
      console.error("Submission error:", error);
      enqueueSnackbar(error.response?.data?.message || "Failed to create account", {
        variant: "error",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleRedirect = () => {
    setShowRedirectModal(false);
    navigate("/candidates");
  };

  const handleCancelRedirect = () => {
    setShowRedirectModal(false);
  };

  const getGridColumns = () => {
    if (isMobile) return '1fr';
    if (isTablet) return 'repeat(2, 1fr)';
    return 'repeat(4, 1fr)';
  };

  return (
    <Box sx={{ px: { xs: 2, sm: 3 }, pt: 2, pb: 12, width: '100%' }}>
      <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', gap: 2 }}>
        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: 2,
            bgcolor: '#f0f7ff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <PersonAdd sx={{ color: '#2469F0', fontSize: 28 }} />
        </Box>
        <Box>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              color: '#0f172a',
              fontSize: { xs: '1.5rem', sm: '2rem' },
              letterSpacing: '-0.02em',
            }}
          >
            Create New User
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              mt: 0.5,
              fontSize: '0.9rem',
              color: '#64748b',
            }}
          >
            Fill in the details to add a new user to the system
          </Typography>
        </Box>
      </Box>

      <form onSubmit={handleSubmit} id="candidate-form">
        <Box sx={{ 
          display: 'grid', 
          gridTemplateColumns: getGridColumns(),
          gap: 2,
          width: '100%',
          mb: 2
        }}>
          <Box>
            <CandidateProfileForm
              handleChange={handleChange}
              handleFileUpload={handleImageUpload}
              data={formData}
            />
          </Box>
          
          <Box>
            <CandidateLocationForm
              handleChange={handleChange}
              data={formData}
              selectedArea={selectedArea}
              handleSelectAreaChange={handleSelectAreaChange}
            />
          </Box>
          
          <Box>
            <CandidateCurrentJobForm
              handleChange={handleChange}
              formData={formData}
            />
          </Box>
          
          <Box>
            <CandidatesSkillForm
              handleChange={handleChange}
              formData={formData}
            />
          </Box>

          <Box>
            <CandidateContactsForm
              handleChange={handleChange}
              data={formData}
            />
          </Box>
          
          <Box>
            <CandidateSocialForm
              handleChange={handleChange}
              formData={formData}
            />
          </Box>
          
          <Box>
            <CandidateLookingFor
              handleChange={handleChange}
              formData={formData}
            />
          </Box>

          <Box>
            <CandidateJobStatusForm
              handleChange={handleChange}
              formData={formData}
            />
          </Box>
        </Box>
      </form>

      <Fab
        variant="extended"
        color="primary"
        aria-label="add"
        type="submit"
        form="candidate-form"
        disabled={submitting}
        sx={{
          position: "fixed",
          bottom: 40,
          right: 40,
          zIndex: 1000,
          borderRadius: 2,
          px: 4,
          py: 2,
          minHeight: 56,
          fontSize: "1rem",
          fontWeight: 600,
          boxShadow: (theme) => theme.shadows[8],
          textTransform: "none",
          "&:hover": {
            boxShadow: (theme) => theme.shadows[12],
          },
        }}
      >
        <AddIcon sx={{ mr: 1 }} />
        {submitting ? "Creating..." : "Add User"}
      </Fab>

      <SuccessModal
        open={showRedirectModal}
        onClose={handleCancelRedirect}
        onConfirm={handleRedirect}
        title="Account Created Successfully!"
        message="A password setup email has been sent to the user. They will be able to set their password and access the system."
        confirmText="View Profile"
        cancelText="Stay Here"
        userName={candidateData?.userName}
        userEmail={candidateData?.userEmail}
        username={candidateData?.username}
        userRole={candidateData?.userRole}
      />
    </Box>
  );
}

export default CandidateForm;